import { SelectionModel } from "@angular/cdk/collections";
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { MatListOption, MatSelectionListChange } from "@angular/material/list";
import { MatSlideToggleChange } from "@angular/material/slide-toggle";
import { MatSnackBar } from "@angular/material/snack-bar";
import * as buffer from "buffer";
import { firstValueFrom, Observable, skipWhile, Subject, zip } from "rxjs";
import { filter, take, takeUntil } from "rxjs/operators";
import {
  CONFIRM_DIALOG_CONFIRM_ACTION,
  CONFIRM_DIALOG_FORM_WRONG_NAME,
  SNACKBAR_FORM_ASSIGN_ERROR,
} from "../../locale/consts.en";
import { DepartmentsService } from "../departments/departments.service";
import { FormsAPIService } from "../forms-permissions/forms-api.service";
import { ChangeFormPermissionsCommand } from "../forms-permissions/forms.model";
import { MasterDataService } from "../master-data/master-data-service";
import { Department, FormType, Status } from "../master-data/master-data.model";
import {
  ConfDialogType,
  ConfirmDialogModel,
} from "../shared/confirm-dialog/confim-dialog.model";
import { ConfirmDialogComponent } from "../shared/confirm-dialog/confirm-dialog.component";
import { DatabaseService } from "../shared/database.service";
import { sortByStringWithEmptyAtEnd } from "../shared/helpers/helper-functions";
import {
  DescriptiveFormTemplate,
  FormTemplate,
  ServiceAttachmentGroupType,
  TemplateFormForm,
} from "./forms.model";
import { FormsService } from "./forms.service";
(window as any).Buffer = buffer.Buffer;

@Component({
  selector: "app-forms",
  templateUrl: "./forms.component.html",
  styleUrls: ["./forms.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  // Outlined, compact fields across the whole form editor (sections,
  // fields, drawings, attachments, tables...), matching the Order details
  // "new UI" look. Colours/shape come from forms.component.scss.
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: "outline", subscriptSizing: "dynamic" },
    },
  ],
})
export class FormsComponent implements OnInit, OnDestroy {
  private formsService = inject(FormsService);
  private snackBar = inject(MatSnackBar);
  private dataBaseService = inject(DatabaseService);
  private masterDataService = inject(MasterDataService);
  private departmentService = inject(DepartmentsService);
  private formsAPIService = inject(FormsAPIService);
  private dialog = inject(MatDialog);

  private destroyed$ = new Subject();
  form = new FormGroup(
    new TemplateFormForm(
      new FormTemplate("new form", [], [], [], [], [], [], [], []),
    ),
  );

  file: Blob;
  base64textString = "";

  editMode: boolean | null = null;
  formId: number | null = null;
  editing = false;

  loadedTemplates$: Observable<DescriptiveFormTemplate[]>;

  statuses: Status[];
  departments: Department[];
  formtypes: FormType[] = [];
  showOnlyActive = true;

  constructor() {
    const formsService = this.formsService;

    this.loadedTemplates$ = formsService.loadedForms$;
  }

  ngOnInit() {
    zip(
      this.masterDataService.getStatuses(),
      this.masterDataService.getFormTypes(),
      this.departmentService.departments$.pipe(
        skipWhile((depts) => depts == null),
      ),
    ).subscribe(([statuses, formtypes, departments]) => {
      this.statuses = statuses.filter(
        (s) =>
          s.metaId.toString().substring(0, 1) === "2" ||
          s.metaId.toString().substring(0, 1) === "3",
      );
      this.formtypes = sortByStringWithEmptyAtEnd(
        formtypes,
        (item) => item.name,
      );
      // this.formtypes = formtypes.sort((a, b) => a.name.localeCompare(b.name));
      this.departments = departments;
    });
    this.getTemplates();
    this.formsService.serviceForm$
      .pipe(
        takeUntil(this.destroyed$),
        filter((val) => val !== undefined && val !== null),
      )
      .subscribe((form: FormGroup) => {
        this.form = form;
        // this.formsService.setServiceForm(this.form);
      });
  }

  getTemplates() {
    this.formsService.getAll();
  }

  createForm() {
    this.editMode = false;
  }

  editForm() {
    this.editMode = true;
  }

  onShowOnlyActiveChange($event: MatSlideToggleChange) {
    this.showOnlyActive = $event.checked;
  }

  onFormSelected(form: FormTemplate | any) {
    if (form !== null) {
      this.form = new FormGroup(new TemplateFormForm(form));
      this.formsService.setServiceForm(this.form);
      this.formId = null;
      this.editing = true;
    } else {
      this.clearRefresh();
    }
  }

  getGroups(form) {
    return form.get("groups").controls;
  }

  getTables(form) {
    return form.get("tables").controls;
  }

  getFields(form) {
    return form.get("fields").controls;
  }

  getSignatures(form) {
    return form.get("signatures").controls;
  }

  getDrawings(form) {
    return form.get("drawings").controls;
  }

  getAttachments(form) {
    return form.get("attachments").controls;
  }

  getAttachmentGroups(form) {
    return form.get("attachmentGroups").controls;
  }

  clearRefresh() {
    this.editMode = null;
    this.formId = null;
    this.editing = false;
    this.formsService.formClear();
    this.base64textString = "";
  }

  openInput() {
    document.getElementById("fileInput").click();
  }

  fileChange(e: Event) {
    this.file = (e.target as HTMLInputElement).files[0];
    this.uploadDocument(this.file);
  }

  uploadDocument(file: Blob) {
    const fileReader = new FileReader();
    fileReader.onload = this.handleReaderLoaded.bind(this);
    fileReader.readAsBinaryString(file);
  }

  handleReaderLoaded(readerEvt) {
    const binaryString = readerEvt.target.result;
    // this.base64textString = btoa(binaryString); //deprecated
    this.base64textString = Buffer.from(binaryString, "binary").toString(
      "base64",
    );
  }

  jsonInput() {
    document.getElementById("jsonInput").click();
  }

  jsonSelected(e: Event) {
    const file = (e.target as HTMLInputElement).files[0];
    this.jsonUpload(file);
  }

  jsonUpload(file) {
    const fileReader = new FileReader();
    fileReader.onload = this.handleJsonLoaded.bind(this);
    fileReader.readAsText(file);
  }

  handleJsonLoaded(readerEvt) {
    const text = readerEvt.target.result;
    this.formsService.loadFormFromFile(text);
    setTimeout(() => {
      this.formId = null;
      this.editing = true;
      this.editForm();
    }, 10);
  }

  toggleAllStatusesSelection($event) {
    if (this.form.controls.allStatuses.value) {
      this.form.controls.statuses.patchValue(this.statuses);
    } else {
      this.form.controls.statuses.patchValue([]);
    }
  }

  onStatusSelection(
    $event: MatSelectionListChange,
    selectedOptions: SelectionModel<MatListOption>,
  ) {
    if (selectedOptions.selected.length === this.statuses.length) {
      this.form.controls.allStatuses.patchValue(true);
    } else {
      this.form.controls.allStatuses.patchValue(false);
    }
  }

  public compareStatuses(c1: Status, c2: Status): boolean {
    return c1?.uuid === c2?.uuid;
  }

  public compareFormTypes(c1: FormType, c2: FormType): boolean {
    return c1?.uuid === c2?.uuid;
  }

  public compareDepartments(c1: FormType, c2: FormType): boolean {
    return c1?.uuid === c2?.uuid;
  }

  toggleAllDepartmentsSelection($event) {
    if (this.form.controls.allDepartments.value) {
      this.form.controls.departments.patchValue(this.departments);
    } else {
      this.form.controls.departments.patchValue([]);
    }
  }

  onDepartmentSelection(
    $event: MatSelectionListChange,
    selectedOptions: SelectionModel<MatListOption>,
  ) {
    if (selectedOptions.selected.length === this.departments.length) {
      this.form.controls.allDepartments.patchValue(true);
    } else {
      this.form.controls.allDepartments.patchValue(false);
    }
  }

  async saveFormToApi(form: FormTemplate) {
    try {
      let copyPermission = false;

      if (form.permissions != null) {
        const dialogData = new ConfirmDialogModel(
          CONFIRM_DIALOG_CONFIRM_ACTION,
          $localize`Do you want to copy the permissions?`,
          ConfDialogType.CONFIRM,
        );
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
          maxWidth: "400px",
          data: dialogData,
        });

        copyPermission = await firstValueFrom(dialogRef.afterClosed());
      }

      const response = await firstValueFrom(this.formsService.addForm(form));
      const isSuccess = response.status === 200 || response.status === 201;

      if (isSuccess) {
        console.log("Save OK!");

        if (response.body?.uuid && copyPermission) {
          const formPermissionsCommand: ChangeFormPermissionsCommand = {
            permissions: form.permissions,
            templateUuid: response.body.uuid,
          };

          try {
            await firstValueFrom(
              this.formsAPIService.formUpdatePermissions(
                formPermissionsCommand,
              ),
            );
          } catch (error) {
            this.snackBar.open(SNACKBAR_FORM_ASSIGN_ERROR, "", {
              duration: 2000,
              panelClass: "app-snackbar-alert",
            });
            console.error("Error by assigning by API", error);
          }
        }
      } else {
        console.error("Save ERROR!");
      }

      this.clearRefresh();
    } catch (error) {
      this.dataBaseService.savingErrorMessage();
    }
  }

  private sortAndArrangeForm(formT: FormTemplate) {
    if (formT.groups != null) {
      formT.groups.sort((a, b) =>
        a.sort > b.sort ? 1 : b.sort > a.sort ? -1 : 0,
      );
      formT.groups.forEach((gr) => {
        if (gr.fields.length > 1) {
          gr.fields.sort((a, b) =>
            a.sort > b.sort ? 1 : b.sort > a.sort ? -1 : 0,
          );
        }
      });
    }
    if (formT.tables != null) {
      formT.tables.sort((a, b) =>
        a.sort > b.sort ? 1 : b.sort > a.sort ? -1 : 0,
      );
      formT.tables.forEach((tab) => {
        if (tab.columns.length > 1) {
          tab.columns.sort((a, b) =>
            a.sort > b.sort ? 1 : b.sort > a.sort ? -1 : 0,
          );
        }
        if (tab.charts != null && tab.charts.length > 1) {
          tab.charts.sort((a, b) =>
            a.sort > b.sort ? 1 : b.sort > a.sort ? -1 : 0,
          );
        }
      });
    }
    if (formT.drawings != null) {
      formT.drawings.sort((a, b) =>
        a.sort > b.sort ? 1 : b.sort > a.sort ? -1 : 0,
      );
      formT.drawings.forEach((dr) => {
        delete dr.blankBkg; // remove not needed field before saving to API
      });
    }
    if (formT.signatures != null) {
      formT.signatures.sort((a, b) =>
        a.sort > b.sort ? 1 : b.sort > a.sort ? -1 : 0,
      );
    }
    if (formT.attachments != null) {
      formT.attachments.sort((a, b) =>
        a.sort > b.sort ? 1 : b.sort > a.sort ? -1 : 0,
      );
    }
    if (formT.attachmentGroups != null) {
      formT.attachmentGroups.sort((a, b) =>
        a.sort > b.sort ? 1 : b.sort > a.sort ? -1 : 0,
      );
      formT.attachmentGroups.forEach((att) => {
        if (att.attachments.length > 1) {
          att.attachments.sort((a, b) =>
            a.sort > b.sort ? 1 : b.sort > a.sort ? -1 : 0,
          );
        }
      });
    }
    if (formT.deviceAttachmentGroups != null) {
      formT.deviceAttachmentGroups.sort((a, b) =>
        a.sort > b.sort ? 1 : b.sort > a.sort ? -1 : 0,
      );
      formT.deviceAttachmentGroups.forEach((att) => {
        if (att.attachments.length > 1) {
          att.attachments.sort((a, b) =>
            a.sort > b.sort ? 1 : b.sort > a.sort ? -1 : 0,
          );
          att.attachments.forEach((devAtt) => {
            devAtt.optional =
              att.type !== ServiceAttachmentGroupType.REQUIRED_ALL;
          });
        }
      });
    }
    if (formT.documents != null) {
      formT.documents.sort((a, b) =>
        a.sort > b.sort ? 1 : b.sort > a.sort ? -1 : 0,
      );
    }
  }

  private cleanValueMap(valueMap: string | null | undefined): string | null {
    if (!valueMap) {
      return null;
    }

    try {
      const parsed = JSON.parse(valueMap) as Record<string, unknown>;

      const cleaned = Object.fromEntries(
        Object.entries(parsed).filter(
          ([, value]) => typeof value === "number" && !Number.isNaN(value),
        ),
      );

      return Object.keys(cleaned).length > 0 ? JSON.stringify(cleaned) : null;
    } catch {
      return null;
    }
  }

  private cleanAllValueMaps(formT: FormTemplate): void {
    formT.tables?.forEach((table) => {
      table.columns?.forEach((column) => {
        column.valueMap = this.cleanValueMap(column.valueMap);
      });
    });
    // numbers of RADIO/DROPDOWN field options, for formula fields
    [
      ...(formT.fields ?? []),
      ...(formT.groups ?? []).flatMap((group) => group.fields ?? []),
    ].forEach((field) => {
      field.valueMap = this.cleanValueMap(field.valueMap);
    });
  }

  saveForm() {
    this.formsService.loadedForms$.pipe(take(1)).subscribe((forms) => {
      const index = forms.findIndex(
        (f) =>
          f.title.trim().toLowerCase() ===
          this.form.value.title.trim().toLowerCase(),
      );
      if (index === -1) {
        const formT = this.form.getRawValue() as unknown as FormTemplate;
        this.sortAndArrangeForm(formT);
        this.cleanAllValueMaps(formT);
        formT.templateBase64 = this.base64textString;
        this.saveFormToApi(formT);
        console.log("Form saved!", formT);
      } else {
        this.snackBar.open(CONFIRM_DIALOG_FORM_WRONG_NAME, "", {
          duration: 2000,
          panelClass: "app-snackbar-alert",
          verticalPosition: "top",
        });
      }
    });
  }
  saveToJSON() {
    const formT = new FormTemplate();
    Object.assign(formT, this.form.getRawValue());
    this.sortAndArrangeForm(formT);
    this.cleanAllValueMaps(formT);
    this.formsService.saveFormToFile(formT.toMap(formT));
    console.log("SAVE to JSON", formT.toMap(formT));
    setTimeout(() => {
      this.clearRefresh();
    }, 10);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(1);
    this.destroyed$.complete();
  }
}
